/**
 * The Supplies dashboard's pieces (CAP-474): what to scan instead of read.
 *
 * Everything shown is admin-api's: a cell's status comes from the flags and
 * dates the consumption model returns, a stay's bar from its dates and guest
 * count, a trend bar from the month's delivered quantity. This file only
 * decides colour, position and order, and every one of those decisions is a
 * pure function with a test.
 */
import { useMemo } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type {
  PortfolioStats,
  SuppliesOverviewCell,
  SuppliesOverviewProperty,
  UpcomingStay,
} from '@/api/client'

export type CellStatus = 'gone' | 'due' | 'fine' | 'unknown'

/** "laundry_detergent" → "Laundry detergent". Labels only; ids stay ids. */
export function categoryLabel(id: string): string {
  const words = id.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export function formatDate(date: string | null): string {
  if (!date) return '—'
  const d = new Date(`${date}T00:00:00`)
  if (Number.isNaN(d.getTime())) return date
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** Whole days from `today` to `date`; negative in the past. */
export function dayOffset(date: string, today: string): number {
  const a = Date.UTC(+today.slice(0, 4), +today.slice(5, 7) - 1, +today.slice(8, 10))
  const b = Date.UTC(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10))
  return Math.round((b - a) / 86_400_000)
}

/** The colour a cell gets, from the model's flags only. */
export function cellStatus(cell: SuppliesOverviewCell, today: string): CellStatus {
  if (cell.confidence === 'none') return 'unknown'
  if (cell.stock_estimate === 0 || (cell.runout_date !== null && cell.runout_date <= today)) {
    return 'gone'
  }
  if (cell.due_within_horizon) return 'due'
  return 'fine'
}

export interface HouseNeeds {
  /** gone, with a stay inside the horizon: the thing to order today */
  order_now: string[]
  /** runs out inside the next 7 days */
  due_week: string[]
  /** runs out later inside the horizon */
  due_later: string[]
  /** gone, but nothing booked in the horizon: nothing to do yet */
  idle_gone: string[]
  /** the soonest runout among order_now and due, today for anything gone */
  first_runout: string | null
}

export function houseNeeds(row: SuppliesOverviewProperty, today: string): HouseNeeds {
  const needs: HouseNeeds = {
    order_now: [],
    due_week: [],
    due_later: [],
    idle_gone: [],
    first_runout: null,
  }
  const weekEnd = addDays(today, 7)
  for (const [category, cell] of Object.entries(row.categories)) {
    const status = cellStatus(cell, today)
    if (status === 'gone') {
      if (cell.stays_ahead > 0) {
        needs.order_now.push(category)
        needs.first_runout = today
      } else needs.idle_gone.push(category)
    } else if (status === 'due' && cell.runout_date) {
      if (cell.runout_date <= weekEnd) needs.due_week.push(category)
      else needs.due_later.push(category)
      if (needs.first_runout === null || cell.runout_date < needs.first_runout) {
        needs.first_runout = cell.runout_date
      }
    }
  }
  return needs
}

export type KpiKey = 'now' | 'week' | 'later' | 'clear'

export interface KpiCounts {
  now: { cells: number; houses: number }
  week: { cells: number; houses: number }
  later: { cells: number; houses: number }
  clear: { houses: number }
}

export function kpiCounts(rows: SuppliesOverviewProperty[], today: string): KpiCounts {
  const counts: KpiCounts = {
    now: { cells: 0, houses: 0 },
    week: { cells: 0, houses: 0 },
    later: { cells: 0, houses: 0 },
    clear: { houses: 0 },
  }
  for (const row of rows) {
    const n = houseNeeds(row, today)
    counts.now.cells += n.order_now.length
    counts.week.cells += n.due_week.length
    counts.later.cells += n.due_later.length
    if (n.order_now.length) counts.now.houses++
    if (n.due_week.length) counts.week.houses++
    if (n.due_later.length) counts.later.houses++
    if (!n.order_now.length && !n.due_week.length && !n.due_later.length) counts.clear.houses++
  }
  return counts
}

export function houseMatches(row: SuppliesOverviewProperty, key: KpiKey, today: string): boolean {
  const n = houseNeeds(row, today)
  switch (key) {
    case 'now':
      return n.order_now.length > 0
    case 'week':
      return n.due_week.length > 0
    case 'later':
      return n.due_later.length > 0
    case 'clear':
      return !n.order_now.length && !n.due_week.length && !n.due_later.length
  }
}

/** Most urgent first: more to order now, then the soonest runout, then the name. */
export function urgencyOrder(
  a: SuppliesOverviewProperty,
  b: SuppliesOverviewProperty,
  today: string
): number {
  const na = houseNeeds(a, today)
  const nb = houseNeeds(b, today)
  if (na.order_now.length !== nb.order_now.length) {
    return nb.order_now.length - na.order_now.length
  }
  const ra = na.first_runout ?? '9999-99-99'
  const rb = nb.first_runout ?? '9999-99-99'
  if (ra !== rb) return ra < rb ? -1 : 1
  return a.name.localeCompare(b.name)
}

/** The next guests a house sees, for the summary column. */
export function nextGuests(stays: UpcomingStay[], today: string): string {
  const next = stays[0]
  if (!next) return 'nothing booked'
  const guests = next.guests === null ? 'guests' : `${next.guests} guests`
  if (next.check_in <= today) return `${guests} in house`
  return `${guests} ${formatDate(next.check_in)}`
}

const STATUS_CHIP: Record<CellStatus, string> = {
  gone: 'bg-[rgba(255,77,79,0.16)] text-danger',
  due: 'bg-warningBg text-amber',
  fine: 'bg-mintGlow text-mint',
  unknown: 'bg-surface2 text-textFaint',
}

export function chipText(cell: SuppliesOverviewCell, status: CellStatus): string {
  switch (status) {
    case 'gone':
      return 'gone'
    case 'due':
      return formatDate(cell.runout_date)
    case 'fine':
      return 'ok'
    case 'unknown':
      return 'no trend'
  }
}

/**
 * The confidence level, short, for the claim it qualifies ("gone · low").
 * A level, not a probability: the model grades itself by how many
 * consumption cycles it has seen and how steady they were, so a
 * percentage would be invented (Andrei asked for "gone 95%", 2026-09-20;
 * a calibrated probability is CAP-484).
 */
export function confidenceShort(confidence: SuppliesOverviewCell['confidence']): string | null {
  switch (confidence) {
    case 'low':
      return 'low'
    case 'medium':
      return 'med'
    case 'high':
      return 'high'
    default:
      return null
  }
}

/** Stock left as a share of the last delivery, for the bar under a chip; null when unknown. */
export function stockShare(cell: SuppliesOverviewCell): number | null {
  if (cell.stock_estimate === null || cell.last_qty <= 0) return null
  return Math.max(0, Math.min(1, cell.stock_estimate / cell.last_qty))
}

export function HeatCell({
  cell,
  today,
  category,
  house,
  portfolio,
  onOpen,
}: {
  cell: SuppliesOverviewCell
  today: string
  category: string
  house: string
  portfolio: PortfolioStats | undefined
  onOpen: () => void
}) {
  const status = cellStatus(cell, today)
  const share = stockShare(cell)
  const label = `${cell.last_qty} delivered ${formatDate(cell.last_delivery_date)}, ${chipText(cell, status)}${
    status === 'gone' && cell.stays_ahead === 0 ? ', nothing booked' : ''
  }. Open the receipt.`
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onOpen}
          aria-label={label.trim()}
          className={`block w-full rounded-sm px-1.5 py-1 text-center text-[11px] font-medium leading-tight hover:brightness-125 focus:outline-hidden focus:ring-2 focus:ring-mint ${STATUS_CHIP[status]} ${
            status === 'gone' && cell.stays_ahead === 0 ? 'opacity-60' : ''
          }`}
        >
          <span className="flex items-center justify-center gap-1 whitespace-nowrap">
            <span>
              {chipText(cell, status)}
              {status !== 'unknown' && confidenceShort(cell.confidence) && (
                <span className="font-normal opacity-70">
                  {' '}
                  · {confidenceShort(cell.confidence)}
                </span>
              )}
            </span>
            {cell.vs_portfolio !== null && (
              <span
                className="rounded-sm px-1 text-[10px] font-semibold tabular-nums"
                style={{
                  background: ratioTone(cell.vs_portfolio).bg,
                  color: ratioTone(cell.vs_portfolio).fg,
                }}
                title={`${ratioWords(cell.vs_portfolio)} for this supply`}
              >
                {cell.vs_portfolio.toFixed(1)}×
              </span>
            )}
          </span>
          {share !== null && status !== 'gone' && (
            <span className="mt-1 block h-0.5 w-full rounded bg-[rgba(255,255,255,0.12)]">
              <span
                className="block h-0.5 rounded bg-current"
                style={{ width: `${Math.round(share * 100)}%` }}
              />
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="w-80 p-0 text-xs">
        <CellCard
          cell={cell}
          today={today}
          category={category}
          house={house}
          status={status}
          portfolio={portfolio}
        />
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * What a house needs, spelled out: the card behind the Needs cell (Andrei,
 * 2026-09-20: "showing what exactly needed so can quickly get it"). Same
 * houseNeeds result the cell's counts come from, nothing new.
 */
export function NeedsCard({
  row,
  needs,
  today,
}: {
  row: SuppliesOverviewProperty
  needs: HouseNeeds
  today: string
}) {
  const line = (category: string) => {
    const cell = row.categories[category]
    const conf = confidenceShort(cell.confidence)
    return `last ${formatDate(cell.last_delivery_date)}, ${cell.last_qty} ${cell.last_qty === 1 ? 'unit' : 'units'}${
      conf ? ` · ${conf} confidence` : ''
    }`
  }
  const dueSoon = [...needs.due_week, ...needs.due_later].sort((a, b) =>
    (row.categories[a].runout_date ?? '').localeCompare(row.categories[b].runout_date ?? '')
  )
  const nothing =
    needs.order_now.length === 0 && dueSoon.length === 0 && needs.idle_gone.length === 0
  // whitespace-normal: the cell this hangs off is nowrap, and the tooltip
  // renders inline rather than in a portal, so it would inherit the clip.
  return (
    <div className="space-y-2 whitespace-normal p-3">
      <div className="text-sm font-semibold text-text">{row.name}</div>
      {needs.order_now.length > 0 && (
        <div>
          <div className="font-semibold uppercase tracking-wide text-danger">Order now</div>
          <ul className="mt-0.5 space-y-1">
            {needs.order_now.map(c => (
              <li key={c}>
                <div className="text-text">{categoryLabel(c)}</div>
                <div className="text-textFaint">{line(c)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {dueSoon.length > 0 && (
        <div>
          <div className="font-semibold uppercase tracking-wide text-amber">Runs out soon</div>
          <ul className="mt-0.5 space-y-1">
            {dueSoon.map(c => (
              <li key={c}>
                <div className="text-text">
                  {categoryLabel(c)}
                  <span className="text-amber"> ~{formatDate(row.categories[c].runout_date)}</span>
                </div>
                <div className="text-textFaint">{line(c)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {needs.idle_gone.length > 0 && (
        <div>
          <div className="font-semibold uppercase tracking-wide text-textFaint">Gone, can wait</div>
          <p className="mt-0.5 text-textMuted">
            {needs.idle_gone.map(categoryLabel).join(', ')}: nothing booked, so no order yet.
          </p>
        </div>
      )}
      {nothing && <p className="text-textMuted">Nothing due at this house.</p>}
      <p className="text-textFaint">
        Next: {nextGuests(row.upcoming_stays, today)} · click the house to plan an order.
      </p>
    </div>
  )
}

const STATUS_WORD: Record<CellStatus, string> = {
  gone: 'likely gone',
  due: 'runs out soon',
  fine: 'fine',
  unknown: 'no trend yet',
}

/** "1.4 per 100 guest-nights · portfolio rate only" or "· this house, 3 cycles". */
export function rateLine(cell: SuppliesOverviewCell): string {
  if (cell.rate === null) return 'no rate yet, one delivery so far'
  const per100 = (cell.rate * 100).toFixed(1)
  const from =
    cell.property_rate === null
      ? 'portfolio rate only'
      : cell.k === 1
        ? 'this house, 1 cycle, blended with the portfolio'
        : `this house, ${cell.k} cycles, blended with the portfolio`
  return `${per100} per 100 guest-nights · ${from}`
}

/** What the model expects, as one short phrase for the card. */
export function expectedLine(
  cell: SuppliesOverviewCell,
  status: CellStatus,
  today: string
): string {
  if (status === 'unknown') return 'no estimate until a second delivery'
  if (status === 'gone') {
    return cell.stays_ahead === 0 ? 'gone, but nothing booked' : `gone, guests coming: order now`
  }
  if (cell.runout_date) {
    const days = dayOffset(cell.runout_date, today)
    return `runs out ~${formatDate(cell.runout_date)}, in ${days} ${days === 1 ? 'day' : 'days'}`
  }
  return 'lasts beyond the bookings on the calendar'
}

/**
 * The tooltip as a card: the facts the model used, in two columns, and the
 * order history for this supply at this house. No sentence to decode
 * (Andrei, 2026-09-20: "make this tooltip look nicer and include
 * historical orders for the thing for that house").
 */
export function CellCard({
  cell,
  today,
  category,
  house,
  status,
  portfolio,
}: {
  cell: SuppliesOverviewCell
  today: string
  category: string
  house: string
  status: CellStatus
  portfolio: PortfolioStats | undefined
}) {
  const daysSince = dayOffset(today, cell.last_delivery_date)
  const tone =
    status === 'gone'
      ? 'text-danger'
      : status === 'due'
        ? 'text-amber'
        : status === 'fine'
          ? 'text-mint'
          : 'text-textFaint'
  return (
    <div className="space-y-2 p-3">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-text">{categoryLabel(category)}</div>
          <div className="text-textMuted">{house}</div>
        </div>
        <span className={`whitespace-nowrap font-medium ${tone}`}>{STATUS_WORD[status]}</span>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="text-textFaint">Last delivery</dt>
        <dd className="text-text">
          {formatDate(cell.last_delivery_date)} · {cell.last_qty}{' '}
          {cell.last_qty === 1 ? 'unit' : 'units'}
          {cell.last_retailer ? ` · ${cell.last_retailer}` : ''}
        </dd>
        <dt className="text-textFaint">Since then</dt>
        <dd className="text-text">
          {cell.guest_nights_since_last} guest-nights over {daysSince}{' '}
          {daysSince === 1 ? 'day' : 'days'}
        </dd>
        <dt className="text-textFaint">Coming</dt>
        <dd className="text-text">
          {cell.stays_ahead === 0
            ? 'nothing booked in the horizon'
            : `${cell.guest_nights_ahead} guest-nights, ${cell.stays_ahead} ${cell.stays_ahead === 1 ? 'stay' : 'stays'}`}
        </dd>
        <dt className="text-textFaint">Expected</dt>
        <dd className={tone}>{expectedLine(cell, status, today)}</dd>
        <dt className="text-textFaint">Rate</dt>
        <dd className="text-text">
          {rateLine(cell)}
          {cell.confidence !== 'none' && (
            <span className={cell.confidence === 'low' ? 'text-amber' : 'text-textMuted'}>
              {' '}
              · {cell.confidence} confidence
            </span>
          )}
        </dd>
      </dl>
      <div>
        <div className="font-medium text-textMuted">
          Usage vs portfolio
          {cell.vs_portfolio !== null && (
            <span
              className="ml-2 rounded-sm px-1.5 py-0.5 font-semibold"
              style={{
                background: ratioTone(cell.vs_portfolio).bg,
                color: ratioTone(cell.vs_portfolio).fg,
              }}
            >
              {ratioWords(cell.vs_portfolio)}
            </span>
          )}
        </div>
        <dl className="mt-0.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          <dt className="text-textFaint">This house</dt>
          <dd className="text-text">
            {cell.property_rate !== null ? per100(cell.property_rate) : 'no rate of its own yet'}
            {cell.cycles.length > 0 && (
              <div className="text-textMuted">
                {cell.own_qty} {cell.own_qty === 1 ? 'unit' : 'units'} over {cell.own_stays}{' '}
                {cell.own_stays === 1 ? 'reservation' : 'reservations'}, {cell.own_guest_nights}{' '}
                guest-nights, {cell.cycles.length} {cell.cycles.length === 1 ? 'cycle' : 'cycles'}
              </div>
            )}
          </dd>
          <dt className="text-textFaint">Portfolio</dt>
          <dd className="text-text">
            {portfolio ? per100(portfolio.rate) : 'no portfolio rate yet'}
            {portfolio && (
              <div className="text-textMuted">
                {portfolio.qty} units over {portfolio.stays} reservations, {portfolio.guest_nights}{' '}
                guest-nights, {portfolio.houses} {portfolio.houses === 1 ? 'house' : 'houses'}
              </div>
            )}
          </dd>
        </dl>
      </div>
      <div>
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-textMuted">Orders at this house</span>
          <DeliverySparkline deliveries={cell.deliveries} today={today} />
        </div>
        <ul className="mt-1 max-h-32 space-y-0.5 overflow-auto">
          {cell.deliveries.slice(0, 8).map(d => (
            <li key={`${d.receipt_id}-${d.date}`} className="flex justify-between gap-3">
              <span className="whitespace-nowrap text-text">
                {formatDate(d.date)} · {d.qty}
                {d.retailer ? <span className="text-textMuted"> · {d.retailer}</span> : null}
              </span>
              <span className="truncate text-textFaint" title={d.name_raw}>
                {d.name_raw}
              </span>
            </li>
          ))}
          {cell.deliveries.length > 8 && (
            <li className="text-textFaint">and {cell.deliveries.length - 8} more</li>
          )}
        </ul>
      </div>
      <p className="text-textFaint">Click for the receipt.</p>
    </div>
  )
}

const KPI_META: Record<KpiKey, { title: string; tone: string; hint: string }> = {
  now: { title: 'Order now', tone: 'text-danger', hint: 'gone, with guests coming' },
  week: { title: 'Due this week', tone: 'text-amber', hint: 'runs out in the next 7 days' },
  later: { title: 'Due later', tone: 'text-amber/80', hint: 'runs out inside the horizon' },
  clear: { title: 'Nothing to do', tone: 'text-mint', hint: 'houses with nothing due' },
}

export function KpiStrip({
  counts,
  active,
  onSelect,
}: {
  counts: KpiCounts
  active: KpiKey | null
  onSelect: (key: KpiKey | null) => void
}) {
  const keys: KpiKey[] = ['now', 'week', 'later', 'clear']
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4" role="group" aria-label="Summary">
      {keys.map(key => {
        const meta = KPI_META[key]
        const n = key === 'clear' ? counts.clear.houses : counts[key].cells
        const sub =
          key === 'clear'
            ? meta.hint
            : `${counts[key].houses} ${counts[key].houses === 1 ? 'house' : 'houses'} · ${meta.hint}`
        const isActive = active === key
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(isActive ? null : key)}
            aria-pressed={isActive}
            className={`rounded-card border p-3 text-left transition-colors focus:outline-hidden focus:ring-2 focus:ring-mint ${
              isActive
                ? 'border-mintBorder bg-mintHover'
                : 'border-border bg-surface hover:bg-surface2'
            }`}
          >
            <div className="text-xs font-medium uppercase tracking-wide text-textMuted">
              {meta.title}
            </div>
            <div
              className={`mt-1 text-2xl font-semibold tabular-nums ${n === 0 ? 'text-textFaint' : meta.tone}`}
            >
              {n}
            </div>
            <div className="mt-0.5 text-xs text-textFaint">{sub}</div>
          </button>
        )
      })}
    </div>
  )
}

export interface TimelineMarker {
  day: number
  categories: string[]
  gone: boolean
}

/** Runouts grouped by day, gone ones on day 0, so a house's row draws one marker per day. */
export function timelineMarkers(
  row: SuppliesOverviewProperty,
  today: string,
  horizonDays: number
): TimelineMarker[] {
  const byDay = new Map<number, TimelineMarker>()
  for (const [category, cell] of Object.entries(row.categories)) {
    const status = cellStatus(cell, today)
    let day: number
    if (status === 'gone') day = 0
    else if (status === 'due' && cell.runout_date) day = dayOffset(cell.runout_date, today)
    else continue
    if (day > horizonDays) continue
    const m = byDay.get(day) ?? { day, categories: [], gone: status === 'gone' }
    m.categories.push(category)
    byDay.set(day, m)
  }
  return [...byDay.values()].sort((a, b) => a.day - b.day)
}

export interface TimelineRow {
  row: SuppliesOverviewProperty
  markers: TimelineMarker[]
}

/**
 * Houses with something running out first, soonest first; then houses with a
 * stay but nothing due; houses with neither are counted, not drawn.
 */
export function timelineRows(
  rows: SuppliesOverviewProperty[],
  today: string,
  horizonDays: number
): { drawn: TimelineRow[]; quiet: number } {
  const drawn: TimelineRow[] = []
  let quiet = 0
  for (const row of rows) {
    const markers = timelineMarkers(row, today, horizonDays)
    const live = markers.filter(m => row.upcoming_stays.length > 0 || !m.gone)
    if (live.length === 0 && row.upcoming_stays.length === 0) {
      quiet++
      continue
    }
    drawn.push({ row, markers: live })
  }
  drawn.sort((a, b) => {
    const fa = a.markers[0]?.day ?? Number.POSITIVE_INFINITY
    const fb = b.markers[0]?.day ?? Number.POSITIVE_INFINITY
    if (fa !== fb) return fa - fb
    const sa = a.row.upcoming_stays[0]?.check_in ?? '9999'
    const sb = b.row.upcoming_stays[0]?.check_in ?? '9999'
    if (sa !== sb) return sa < sb ? -1 : 1
    return a.row.name.localeCompare(b.row.name)
  })
  return { drawn, quiet }
}

function pct(days: number, horizonDays: number): string {
  return `${(Math.max(0, Math.min(horizonDays, days)) / horizonDays) * 100}%`
}

export function HorizonTimeline({
  rows,
  today,
  horizonDays,
  onSelect,
}: {
  rows: SuppliesOverviewProperty[]
  today: string
  horizonDays: number
  onSelect: (propertyId: string) => void
}) {
  const { drawn, quiet } = useMemo(
    () => timelineRows(rows, today, horizonDays),
    [rows, today, horizonDays]
  )
  const ticks = [0, 7, 14, 21].filter(t => t <= horizonDays)
  const grid = {
    backgroundImage: `repeating-linear-gradient(to right, rgba(255,255,255,0.06) 0, rgba(255,255,255,0.06) 1px, transparent 1px, transparent calc(100% / ${horizonDays}))`,
  }
  return (
    <div>
      <div className="flex items-center text-[10px] uppercase tracking-wide text-textFaint">
        <div className="w-28 shrink-0 sm:w-40" />
        <div className="w-8 shrink-0" />
        <div className="relative h-4 flex-1">
          {ticks.map((t, i) => (
            <span
              key={t}
              className={`absolute whitespace-nowrap ${
                i === 0
                  ? ''
                  : i === ticks.length - 1
                    ? '-translate-x-full'
                    : 'hidden -translate-x-1/2 sm:inline'
              }`}
              style={{ left: pct(t, horizonDays) }}
            >
              {t === 0 ? 'Today' : formatDate(addDays(today, t))}
            </span>
          ))}
        </div>
      </div>
      <ul className="space-y-1">
        {drawn.map(({ row, markers }) => {
          const gone = markers.find(m => m.gone)
          const dated = markers.filter(m => !m.gone)
          return (
            <li key={row.property_id} className="flex items-center">
              <button
                type="button"
                onClick={() => onSelect(row.property_id)}
                className="w-28 shrink-0 truncate rounded-sm pr-2 text-left text-xs font-medium text-text hover:text-mint focus:outline-hidden focus:ring-2 focus:ring-mint sm:w-40"
                title={row.name}
              >
                {row.name}
              </button>
              <div className="flex w-8 shrink-0 items-center justify-center">
                {gone && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => onSelect(row.property_id)}
                        aria-label={`${gone.categories.map(categoryLabel).join(', ')} gone`}
                        className="flex h-5 min-w-5 items-center justify-center rounded-full bg-danger px-1.5 text-[11px] font-semibold text-bg focus:outline-hidden focus:ring-2 focus:ring-mint"
                      >
                        {gone.categories.length}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs">
                      Already gone: {gone.categories.map(categoryLabel).join(', ')}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              <div
                className="relative h-7 flex-1 overflow-hidden rounded-sm bg-[rgba(255,255,255,0.02)]"
                style={grid}
                role="img"
                aria-label={`${row.name}: ${row.upcoming_stays.length} ${row.upcoming_stays.length === 1 ? 'stay' : 'stays'} in the next ${horizonDays} days${
                  markers.length
                    ? `; ${markers.map(m => `${m.categories.map(categoryLabel).join(', ')} ${m.gone ? 'gone' : `out ${formatDate(addDays(today, m.day))}`}`).join('; ')}`
                    : ''
                }`}
              >
                {row.upcoming_stays.map(s => {
                  const start = Math.max(0, dayOffset(s.check_in, today))
                  const end = Math.min(horizonDays, dayOffset(s.check_out, today))
                  if (end <= start) return null
                  const nights = dayOffset(s.check_out, today) - dayOffset(s.check_in, today)
                  return (
                    <div
                      key={`${s.check_in}-${s.check_out}`}
                      className="absolute bottom-1 top-1 overflow-hidden whitespace-nowrap rounded-sm border border-mintBorder bg-mintGlow px-1 text-[10px] leading-5 text-mint"
                      style={{
                        left: pct(start, horizonDays),
                        width: pct(end - start, horizonDays),
                      }}
                      title={`${formatDate(s.check_in)} to ${formatDate(s.check_out)}, ${nights} ${nights === 1 ? 'night' : 'nights'}${
                        s.guests !== null ? `, ${s.guests} guests` : ''
                      }`}
                    >
                      {end - start >= 3 && s.guests !== null
                        ? `${s.guests} guests`
                        : (s.guests ?? '')}
                    </div>
                  )
                })}
                {dated.map(m => (
                  <Tooltip key={m.day}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => onSelect(row.property_id)}
                        aria-label={`${m.categories.map(categoryLabel).join(', ')} ${m.gone ? 'gone' : `run out ${formatDate(addDays(today, m.day))}`}`}
                        className={`absolute top-1/2 flex h-4 min-w-4 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-bg shadow focus:outline-hidden focus:ring-2 focus:ring-mint ${
                          m.gone ? 'bg-danger' : 'bg-amber'
                        }`}
                        style={{ left: pct(m.day, horizonDays) }}
                      >
                        {m.categories.length > 1 ? m.categories.length : ''}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs">
                      {m.categories.map(categoryLabel).join(', ')}{' '}
                      {m.gone
                        ? 'already gone'
                        : `expected out ${formatDate(addDays(today, m.day))}`}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </li>
          )
        })}
      </ul>
      {quiet > 0 && (
        <p className="mt-2 text-xs text-textFaint">
          {quiet} {quiet === 1 ? 'house has' : 'houses have'} nothing booked and nothing due in the
          next {horizonDays} days.
        </p>
      )}
    </div>
  )
}

/** Colour for a house-over-portfolio ratio: blue below 1, red above, on a log scale so 0.5x and 2x are equally strong. */
export function ratioTone(ratio: number): { bg: string; fg: string } {
  const t = Math.max(-1, Math.min(1, Math.log2(ratio) / 2)) // 0.25x .. 4x -> -1 .. 1
  if (Math.abs(t) < 0.12) return { bg: 'rgba(255,255,255,0.06)', fg: 'rgba(255,255,255,0.7)' }
  return t > 0
    ? { bg: `rgba(255,77,79,${(0.14 + 0.5 * t).toFixed(2)})`, fg: '#ffd7d7' }
    : { bg: `rgba(74,144,226,${(0.14 + 0.5 * -t).toFixed(2)})`, fg: '#d6e6fb' }
}

/** "2.3x the portfolio", "about the portfolio", "0.6x the portfolio" */
export function ratioWords(ratio: number): string {
  if (ratio >= 0.9 && ratio <= 1.1) return 'about the portfolio'
  return `${ratio.toFixed(1)}× the portfolio`
}

export function per100(rate: number): string {
  return `${(rate * 100).toFixed(1)} per 100 guest-nights`
}

/** A year of deliveries as dots on a line, sized by quantity: the shape of a house's usage at a glance. */
export function DeliverySparkline({
  deliveries,
  today,
}: {
  deliveries: Array<{ date: string; qty: number }>
  today: string
}) {
  const width = 120
  const days = 365
  const start = addDays(today, -days)
  const points = deliveries
    .filter(d => d.date >= start && d.date <= today)
    .map(d => ({
      x: (dayOffset(d.date, start) / days) * width,
      r: 2 + Math.min(6, d.qty) * 0.5,
      d,
    }))
  return (
    <svg
      width={width}
      height={16}
      viewBox={`0 0 ${width} 16`}
      role="img"
      aria-label={`${points.length} deliveries in the last year`}
      className="shrink-0"
    >
      <line x1={0} y1={8} x2={width} y2={8} stroke="rgba(255,255,255,0.12)" />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={8} r={p.r} fill="#00ba82" opacity={0.85}>
          <title>{`${formatDate(p.d.date)}: ${p.d.qty}`}</title>
        </circle>
      ))}
    </svg>
  )
}
